import React, { useState } from 'react';
import { fetchAndParseFeed } from '../utils/rssParser';
import { db } from '../utils/db';
import { searchPodcasts, PodcastSearchResult } from '../utils/podcastSearch';

interface AddPodcastFormProps {
  onPodcastAdded: () => void;
  onClose: () => void;
}

const AddPodcastForm: React.FC<AddPodcastFormProps> = ({ onPodcastAdded, onClose }) => {
  const [mode, setMode] = useState<'search' | 'url'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PodcastSearchResult[]>([]);
  const [feedUrl, setFeedUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setSearching(true);
    setError('');
    try {
      const results = await searchPodcasts(searchQuery);
      setSearchResults(results);
      if (results.length === 0) {
        setError('No podcasts found. Try a different search term.');
      }
    } catch (err) {
      setError('Failed to search podcasts. Please try again.');
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectPodcast = async (result: PodcastSearchResult) => {
    setLoading(true);
    setError('');

    try {
      const { podcast, episodes } = await fetchAndParseFeed(result.feedUrl);
      await db.addPodcast(podcast);
      await db.addEpisodes(episodes);
      onPodcastAdded();
      onClose();
    } catch (err) {
      setError('Failed to add podcast. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUrl = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { podcast, episodes } = await fetchAndParseFeed(feedUrl);
      await db.addPodcast(podcast);
      await db.addEpisodes(episodes);
      onPodcastAdded();
      onClose();
    } catch (err) {
      setError('Failed to add podcast. Please check the URL and try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Add Podcast</h2>

        <div className="tabs">
          <button
            className={`tab ${mode === 'search' ? 'active' : ''}`}
            onClick={() => setMode('search')}
          >
            Search
          </button>
          <button
            className={`tab ${mode === 'url' ? 'active' : ''}`}
            onClick={() => setMode('url')}
          >
            RSS URL
          </button>
        </div>

        {mode === 'search' ? (
          <div className="search-mode">
            <form onSubmit={handleSearch}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for a podcast..."
                disabled={searching || loading}
                className="feed-input"
              />
              <button type="submit" disabled={searching || loading} className="submit-btn search-btn">
                {searching ? 'Searching...' : 'Search'}
              </button>
            </form>

            {error && <div className="error-message">{error}</div>}

            {searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((result) => (
                  <div key={result.id} className="search-result-item">
                    <img src={result.imageUrl} alt={result.title} className="podcast-thumbnail" />
                    <div className="result-info">
                      <div className="result-title">{result.title}</div>
                      <div className="result-author">{result.author}</div>
                    </div>
                    <button
                      onClick={() => handleSelectPodcast(result)}
                      disabled={loading}
                      className="add-result-btn"
                    >
                      {loading ? '...' : 'Add'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="url-mode">
            <form onSubmit={handleSubmitUrl}>
              <input
                type="url"
                value={feedUrl}
                onChange={(e) => setFeedUrl(e.target.value)}
                placeholder="Enter RSS feed URL"
                required
                disabled={loading}
                className="feed-input"
              />
              {error && <div className="error-message">{error}</div>}
              <div className="form-actions">
                <button type="submit" disabled={loading} className="submit-btn">
                  {loading ? 'Adding...' : 'Add'}
                </button>
                <button type="button" onClick={onClose} className="cancel-btn">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddPodcastForm;
